import type { Student } from '~/@entities/Student'
import type {
  CountRecommendedDailyWorksheetProblemData,
  CreateDailyWorksheetData,
  CreateDailyWorksheetFilterSettingRequest,
  DailyWorksheetUsedUpdateRequest,
  GetDailyStudentWorksheetScoringByGradeData,
  GetDailyStudentWorksheetScoringByGradeParams,
  GetDailyStudentWorksheetScoringByLessonClassData,
  GetDailyStudentWorksheetScoringByLessonClassParams,
  GetDailyWorksheetFilterSettingData,
  GetDailyWorksheetFilterSettingParams,
  GetDailyWorksheetsData,
  GetDailyWorksheetsParams,
  GetDailyWorksheetStatisticsData,
  GetDailyWorksheetStatisticsParams,
  GetDailyWorksheetStatisticsRankingData,
  GetDailyWorksheetUsedSettingsData,
  Pageable,
  SaveDailyLearningTimeData,
  SaveDailyLearningTimeParams,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'
import type { WithNullableOptionals } from '~/http/request'
import type { Slice } from '~/schema/slice'

import {
  adaptCreatedDailyWorksheetId,
  adaptDailyLearningAccessSettings,
  adaptDailyLearningFilterSetting,
  adaptDailyLearningProblemCount,
  adaptDailyLearningRankings,
  adaptDailyLearningScorings,
  adaptDailyLearningStatistics,
  adaptDailyWorksheetDays,
} from './adapters'
import type {
  DailyLearningAccessSettings,
  DailyLearningFilterSetting,
  DailyLearningRanking,
  DailyLearningScoring,
  DailyLearningStatistic,
  DailyWorksheetDay,
} from './schemas'

// 오늘의 학습 엔드포인트는 모두 CommonResponse 봉투 없이 본문을 그대로 낸다(컨트롤러가 응답 클래스를 바로 반환).
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 student/controller/StudentDailyWorksheetSettingController.kt·
// worksheet/controller/DailyWorksheetFilterSettingController.kt·statistics/controller/DailyWorksheetStatisticsController.kt·
// studentWorksheet/controller/DailyWorksheetScoringController.kt 로 확인.

/***************** 학생별 사용 여부 *****************/

export const getDailyLearningAccessSettings = async (
  meta?: RequestMeta,
): Promise<DailyLearningAccessSettings> => {
  const data = await httpClient().get<GetDailyWorksheetUsedSettingsData>(
    '/daily-worksheet/used-setting',
    { meta },
  )
  return adaptDailyLearningAccessSettings(data)
}

/** 학생 여러 명의 사용 여부를 한꺼번에 바꾼다. 학원의 재원 학생만 반영된다 */
export type DailyLearningAccessBody = DailyWorksheetUsedUpdateRequest

export const updateDailyLearningAccess = async (
  body: DailyLearningAccessBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().put<unknown>('/daily-worksheet/used-setting', body, { meta })
}

/***************** 출제 설정 *****************/

// settingType 에 따라 studentId(STUDENT)·schoolType+grade(GRADE)·lessonClassId(LESSON_CLASS) 중 하나가 필요하다
export type DailyLearningFilterQuery = GetDailyWorksheetFilterSettingParams

export const getDailyLearningFilterSetting = async (
  params: DailyLearningFilterQuery,
  meta?: RequestMeta,
): Promise<DailyLearningFilterSetting> => {
  const data = await httpClient().get<GetDailyWorksheetFilterSettingData>(
    '/daily-worksheet/filter-setting',
    { params, meta },
  )
  return adaptDailyLearningFilterSetting(data)
}

// 선택 필드는 서버가 nullable 로 받고, 오답 학습을 끄면 오답 학습 세 필드를 null 로 보낸다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 worksheet/controller/request/CreateDailyWorksheetFilterSettingRequest.kt 로 확인.
export type DailyLearningFilterBody =
  WithNullableOptionals<CreateDailyWorksheetFilterSettingRequest>

export const saveDailyLearningFilterSetting = async (
  body: DailyLearningFilterBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<unknown>('/daily-worksheet/filter-setting', body, { meta })
}

/** 지금 설정으로 출제할 수 있는 문제 수. 개별 설정 미리보기라 studentIds 의 첫 학생을 기준으로 센다 */
export const countDailyLearningProblems = async (
  body: DailyLearningFilterBody,
  meta?: RequestMeta,
): Promise<number> => {
  const data = await httpClient().post<CountRecommendedDailyWorksheetProblemData>(
    '/daily-worksheet/filter-setting/problem-count',
    body,
    { meta },
  )
  return adaptDailyLearningProblemCount(data)?.count ?? 0
}

/***************** 통계 *****************/

// 생성 타입은 Spring Pageable 을 `pageable` 객체로 그리지만 실제 쿼리는 page·size·sort 를 평탄하게 받는다.
// 조회 기간은 1개월까지다.
export type DailyLearningStatisticsParams = Omit<GetDailyWorksheetStatisticsParams, 'pageable'> &
  Pageable

export const getDailyLearningStatistics = async (
  params: DailyLearningStatisticsParams,
  meta?: RequestMeta,
): Promise<Slice<DailyLearningStatistic>> => {
  const data = await httpClient().get<GetDailyWorksheetStatisticsData>(
    '/daily-worksheet/statistics',
    { params, meta },
  )
  return adaptDailyLearningStatistics(data)
}

// Spring Sort 한 값은 `속성,방향` 이다. 서버 기본값은 정답 수 내림차순이다.
export type DailyLearningRankingSort = `${'correctAnswerCount' | 'wrongAnswerCount'},${
  | 'ASC'
  | 'DESC'}`

export const getDailyLearningRankings = async (
  sort: DailyLearningRankingSort,
  meta?: RequestMeta,
): Promise<DailyLearningRanking[]> => {
  const data = await httpClient().get<GetDailyWorksheetStatisticsRankingData>(
    '/daily-worksheet/statistics/ranking',
    { params: { sort: [sort] }, meta },
  )
  return adaptDailyLearningRankings(data)
}

/***************** 채점 현황 *****************/

// date(yyyy-MM-dd)를 빼면 서버가 오늘로 본다. 학생 이름순으로 정렬해 낸다.
export type DailyLearningScoringParams = Omit<
  GetDailyStudentWorksheetScoringByGradeParams,
  'pageable' | 'schoolType' | 'grade'
> &
  Pageable

export const getDailyLearningScoringsByGrade = async (
  { schoolType, grade }: Pick<GetDailyStudentWorksheetScoringByGradeParams, 'schoolType' | 'grade'>,
  params: DailyLearningScoringParams,
  meta?: RequestMeta,
): Promise<Slice<DailyLearningScoring>> => {
  const data = await httpClient().get<GetDailyStudentWorksheetScoringByGradeData>(
    `/daily-worksheet/grade/${schoolType}/${grade}/scoring`,
    { params, meta },
  )
  return adaptDailyLearningScorings(data)
}

export const getDailyLearningScoringsByLessonClass = async (
  lessonClassId: GetDailyStudentWorksheetScoringByLessonClassParams['lessonClassId'],
  params: DailyLearningScoringParams,
  meta?: RequestMeta,
): Promise<Slice<DailyLearningScoring>> => {
  const data = await httpClient().get<GetDailyStudentWorksheetScoringByLessonClassData>(
    `/daily-worksheet/lesson-class/${lessonClassId}/scoring`,
    { params, meta },
  )
  return adaptDailyLearningScorings(data)
}

/** 학생이 오늘의 학습지를 푼 시간(분)을 쌓는다 */
export const recordDailyLearningTime = async (
  minute: SaveDailyLearningTimeParams['minute'],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<SaveDailyLearningTimeData>('/daily-worksheet/learning-time', undefined, {
    params: { minute },
    meta,
  })
}

/***************** 학생 홈 *****************/
// 두 요청은 CommonResponse 봉투에 담겨 온다.
export type DailyWorksheetWeekParams = Pick<GetDailyWorksheetsParams, 'startDate' | 'endDate'>

/** 요일별 오늘의 학습. 기간을 빼면 이번 주다 */
export const getDailyWorksheetDays = async (
  studentId: Student['id'],
  params?: DailyWorksheetWeekParams,
  meta?: RequestMeta,
): Promise<DailyWorksheetDay[]> => {
  const body = await httpClient().get<GetDailyWorksheetsData>(`/daily-worksheet/${studentId}`, {
    params,
    meta,
  })
  return adaptDailyWorksheetDays(body.data)
}

/** 오늘의 학습지를 만들고 새 학생 학습지 id 를 받는다 */
export const createDailyWorksheet = async (
  meta?: RequestMeta,
): Promise<DailyWorksheetDay['studentWorksheetId']> => {
  const body = await httpClient().post<CreateDailyWorksheetData>('/daily-worksheet', undefined, {
    meta,
  })
  return adaptCreatedDailyWorksheetId(body.data)
}
